import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Howl } from 'howler';
import { FaGuitar, FaVolumeUp } from 'react-icons/fa';

// Instrument data
const instruments = [
  {
    id: 'guitar',
    name: 'Guitarra',
    englishName: 'Guitar',
    color: 'red-500',
    hoverColor: 'red-400',
    activeGradient: 'from-red-500/30 to-red-700/30',
    sound: '/sounds/guitar.mp3',
    icon: (active: boolean) => <FaGuitar className={`w-12 h-12 ${active ? 'text-red-400' : 'text-red-500'}`} />
  },
  {
    id: 'piano',
    name: 'Piano',
    englishName: 'Piano',
    color: 'blue-500',
    hoverColor: 'blue-400',
    activeGradient: 'from-blue-500/30 to-blue-700/30',
    sound: '/sounds/piano.mp3',
    icon: (active: boolean) => (
      <svg className={`w-12 h-12 ${active ? 'text-blue-400' : 'text-blue-500'}`} viewBox="0 0 24 24" fill="currentColor">
        <path d="M22 2H2v20h20V2zm-2 18H4V4h16v16zM8 5H5v10h3V5zm1 10h2V5H9v10zm3 0h2V5h-2v10zm3 0h3V5h-3v10zm-9 1h2v4H6v-4zm3 0h2v4H9v-4zm3 0h2v4h-2v-4zm3 0h2v4h-2v-4z"/>
      </svg>
    )
  },
  {
    id: 'maracas',
    name: 'Maracas',
    englishName: 'Maracas',
    color: 'green-500',
    hoverColor: 'green-400',
    activeGradient: 'from-green-500/30 to-green-700/30',
    sound: '/sounds/maracas.mp3',
    icon: (active: boolean) => (
      <svg className={`w-12 h-12 ${active ? 'text-green-400' : 'text-green-500'}`} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-2-9.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5zm4 0c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5z"/>
      </svg>
    )
  },
  {
    id: 'trumpet',
    name: 'Trompeta',
    englishName: 'Trumpet',
    color: 'red-500',
    hoverColor: 'red-400',
    activeGradient: 'from-red-500/30 to-red-700/30',
    sound: '/sounds/trumpet.mp3',
    icon: (active: boolean) => (
      <svg className={`w-12 h-12 ${active ? 'text-red-400' : 'text-red-500'}`} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6zm-2 16c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/>
      </svg>
    )
  },
  {
    id: 'drums',
    name: 'Tambores',
    englishName: 'Drums',
    color: 'purple-500',
    hoverColor: 'purple-400',
    activeGradient: 'from-purple-500/30 to-purple-700/30',
    sound: '/sounds/drums.mp3',
    icon: (active: boolean) => (
      <svg className={`w-12 h-12 ${active ? 'text-purple-400' : 'text-purple-500'}`} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-2-9.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5zm4 0c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5z"/>
      </svg>
    )
  },
  {
    id: 'microphone',
    name: 'Micrófono',
    englishName: 'Microphone',
    color: 'pink-500',
    hoverColor: 'pink-400',
    activeGradient: 'from-pink-500/30 to-pink-700/30',
    sound: '/sounds/microphone.mp3',
    icon: (active: boolean) => (
      <svg className={`w-12 h-12 ${active ? 'text-pink-400' : 'text-pink-500'}`} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5zm6 6c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
      </svg>
    )
  }
];

const InstrumentGrid: React.FC = () => {
  const [activeInstruments, setActiveInstruments] = useState<{[key: string]: boolean}>({});
  const [visualizerActive, setVisualizerActive] = useState(false);
  const [hoveredInstrument, setHoveredInstrument] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const soundsRef = useRef<{[key: string]: Howl}>({});

  // Initialize sounds
  useEffect(() => {
    // Create Howl instances for each instrument
    instruments.forEach(instrument => {
      soundsRef.current[instrument.id] = new Howl({
        src: [instrument.sound],
        volume: 0.7,
        onend: () => {
          setActiveInstruments(prev => ({
            ...prev,
            [instrument.id]: false
          }));
        }
      });
    });

    return () => {
      // Stop all sounds when component unmounts
      Object.values(soundsRef.current).forEach(sound => sound.stop());
    };
  }, []);

  // Play instrument sound on hover
  useEffect(() => {
    if (hoveredInstrument) {
      playInstrumentSound(hoveredInstrument);
    }
  }, [hoveredInstrument]);

  // Play instrument sound
  const playInstrumentSound = (instrumentId: string) => {
    // Don't stop other sounds, just play this one
    if (soundsRef.current[instrumentId]) {
      // If this sound is already playing, stop it first to restart
      if (soundsRef.current[instrumentId].playing()) {
        soundsRef.current[instrumentId].stop();
      }
      
      soundsRef.current[instrumentId].play();
      
      setActiveInstruments(prev => ({
        ...prev,
        [instrumentId]: true
      }));
      
      setVisualizerActive(true);
    }
  };

  // Create mouse follow effect for hovered instrument
  useEffect(() => {
    if (!hoveredInstrument || !containerRef.current) return;
    
    const handleMouseMove = (e: MouseEvent) => {
      const container = containerRef.current;
      if (!container) return;
      
      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      const glowElements = document.querySelectorAll(`.glow-effect-${hoveredInstrument}`);
      glowElements.forEach(el => {
        (el as HTMLElement).style.background = `radial-gradient(circle at ${x}px ${y}px, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 70%)`;
      });
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [hoveredInstrument]);

  return (
    <div ref={containerRef} className="relative">
      <motion.div 
        className="relative"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, delay: 0.6 }}
      >
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 relative">
          {instruments.map((instrument) => (
            <motion.div 
              key={instrument.id}
              className={`instrument-container relative transform-3d overflow-hidden rounded-2xl backdrop-blur-sm border transition-all duration-300 cursor-pointer
                ${activeInstruments[instrument.id] 
                  ? `bg-gradient-to-br ${instrument.activeGradient} border-${instrument.color}/70 shadow-lg shadow-${instrument.color}/20` 
                  : `bg-gradient-to-br from-gray-800/50 to-gray-900/50 border-gray-700 hover:border-${instrument.color}/50`}
              `}
              whileHover={{ 
                y: -8,
                scale: 1.05,
                transition: { duration: 0.2 }
              }}
              onMouseEnter={() => setHoveredInstrument(instrument.id)}
              onMouseLeave={() => setHoveredInstrument(null)}
            >
              {/* Glow effect overlay */}
              <div className={`absolute inset-0 pointer-events-none glow-effect-${instrument.id}`}></div>
              
              {/* Glass reflection effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none"></div>
              
              <div className="relative p-6 flex flex-col items-center justify-center">
                {/* Instrument icon with subtle animation */}
                <motion.div
                  animate={activeInstruments[instrument.id] ? {
                    scale: [1, 1.1, 1],
                    transition: { repeat: Infinity, duration: 0.8 }
                  } : {}}
                >
                  {instrument.icon(activeInstruments[instrument.id])}
                </motion.div>
                
                {/* Instrument name with gradient effect */}
                <div className="mt-3 text-center">
                  <span className={`block font-medium text-lg bg-gradient-to-r from-${instrument.color} to-${instrument.hoverColor} bg-clip-text text-transparent`}>
                    {instrument.name}
                  </span>
                  <span className="block text-xs text-gray-400">
                    {instrument.englishName}
                  </span>
                </div>
                
                {/* Sound indicator when active */}
                {activeInstruments[instrument.id] && (
                  <div className="absolute top-2 right-2">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className={`flex items-center justify-center w-6 h-6 rounded-full bg-${instrument.color}/20`}
                    >
                      <FaVolumeUp className={`w-3 h-3 text-${instrument.color}`} />
                    </motion.div>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
        
        {/* Music visualizer effect */}
        <AnimatePresence>
          {visualizerActive && Object.keys(activeInstruments).some(id => activeInstruments[id]) && (
            <>
              {/* Floating musical notes for each active instrument */}
              {Object.entries(activeInstruments).map(([id, isActive]) => {
                if (!isActive) return null;
                
                const instrument = instruments.find(ins => ins.id === id);
                if (!instrument) return null;
                
                return [...Array(5)].map((_, i) => (
                  <motion.div
                    key={`note-${id}-${i}`}
                    className={`absolute text-${instrument.color}`}
                    initial={{ 
                      x: `${50 + (Math.random() * 20 - 10)}%`, 
                      y: `${50 + (Math.random() * 20 - 10)}%`,
                      opacity: 0,
                      scale: 0,
                      rotate: Math.random() * 20 - 10
                    }}
                    animate={{ 
                      x: `${50 + (Math.random() * 100 - 50)}%`,
                      y: `${50 - (Math.random() * 100 + 20)}%`,
                      opacity: [0, 1, 0],
                      scale: [0, Math.random() * 0.5 + 0.8, 0],
                      rotate: Math.random() * 360
                    }}
                    exit={{ opacity: 0, scale: 0 }}
                    transition={{ 
                      duration: Math.random() * 1.5 + 1.5,
                      ease: "easeOut"
                    }}
                    style={{ 
                      fontSize: `${Math.random() * 2 + 1.5}rem`,
                      filter: `drop-shadow(0 0 5px var(--tw-text-opacity))` 
                    }}
                  >
                    {['♪', '♫', '♬', '♩'][Math.floor(Math.random() * 4)]}
                  </motion.div>
                ));
              })}
              
              {/* Circular wave effects for each active instrument */}
              {Object.entries(activeInstruments).map(([id, isActive]) => {
                if (!isActive) return null;
                
                const instrument = instruments.find(ins => ins.id === id);
                if (!instrument) return null;
                
                return [...Array(2)].map((_, i) => (
                  <motion.div
                    key={`wave-${id}-${i}`}
                    className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-${instrument.color}/30`}
                    initial={{ width: 0, height: 0, opacity: 0.8 }}
                    animate={{ 
                      width: ['0%', '150%'], 
                      height: ['0%', '150%'],
                      opacity: [0.8, 0]
                    }}
                    transition={{ 
                      duration: 2,
                      delay: i * 0.4,
                      ease: "easeOut"
                    }}
                  ></motion.div>
                ));
              })}
            </>
          )}
        </AnimatePresence>
      </motion.div>
      
      {/* Instrument name tooltip that appears on hover */}
      <AnimatePresence>
        {hoveredInstrument && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`absolute left-1/2 -bottom-16 transform -translate-x-1/2 bg-${instruments.find(ins => ins.id === hoveredInstrument)?.color || 'yellow-400'} text-black px-4 py-2 rounded-full font-medium text-sm`}
          >
            {instruments.find(ins => ins.id === hoveredInstrument)?.englishName}!
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default InstrumentGrid;